/**
 * Calendário e linha do tempo do setor conforme a entidade escolhida
 * (ambientes, lotes, tarefas ou PEÇAS DESMEMBRADAS). Puro: a página passa o
 * que já filtrou.
 */
import { plural } from "../../../utils/dinheiro";
import { eventosDoCalendario } from "../dados/calendario";
import { agruparPorLote } from "../dados/lotes";
import { montarTimelineSetor } from "../dados/timeline";
import { ROTULO_STATUS_PECA, TOM_STATUS_PECA } from "../dados/vocabulario";

export const VOCAB_AMBIENTE = Object.freeze({
  item: ["ambiente", "ambientes"],
  subitem: ["ambiente", "ambientes"],
  subitemTitulo: "Ambientes",
  grupoTitulo: "Clientes",
  previsao: ["previsão", "previsões"],
});

const VOCAB_LOTE = Object.freeze({
  item: ["lote", "lotes"],
  subitem: ["lote", "lotes"],
  subitemTitulo: "Lotes",
  grupoTitulo: "Clientes",
  previsao: ["previsão", "previsões"],
});

const VOCAB_TAREFA = Object.freeze({
  item: ["tarefa", "tarefas"],
  subitem: ["tarefa", "tarefas"],
  subitemTitulo: "Tarefas",
  grupoTitulo: "Ambientes",
  previsao: ["previsão", "previsões"],
});

const VOCAB_PECA = Object.freeze({
  item: ["peça", "peças"],
  subitem: ["peça", "peças"],
  subitemTitulo: "Peças desmembradas",
  grupoTitulo: "Ambientes de origem",
  previsao: ["previsão", "previsões"],
});

function menorData(datas) {
  return datas.filter(Boolean).reduce((menor, d) => (!menor || d < menor ? d : menor), null);
}

function eventoDoLote(lote, base) {
  const date = base === "prevista" ? menorData(lote.demandas.map((d) => d.prevista)) : lote.prazoEfetivo;
  if (!date) return null;
  const clientes = [...new Set(lote.demandas.map((d) => d.cliente).filter(Boolean))];
  let tone = "neutral";
  if (lote.atrasadas > 0) tone = "danger";
  else if (lote.emProducao > 0) tone = "info";
  return {
    id: `lote:${lote.chave}`,
    date,
    title: `${lote.rotulo} · ${plural(lote.demandas.length, "ambiente", "ambientes")}`,
    subtitle: lote.atrasadas ? plural(lote.atrasadas, "atrasado", "atrasados") : "",
    groupLabel: clientes.length === 1 ? clientes[0] : plural(clientes.length, "cliente", "clientes"),
    tone,
    previsao: base === "prevista",
    payload: { tipo: "lote", lote },
  };
}

/**
 * A peça combinada já traz `prevista` e `prazoEfetivo` como Date (com o prazo
 * herdado do ambiente quando ela não tem o próprio) — não é preciso converter
 * nada aqui.
 */
function eventoDaPeca(peca, base) {
  const date = base === "prevista" ? peca.prevista : peca.prazoEfetivo;
  if (!date) return null;
  return {
    id: `peca:${peca.id}`,
    date,
    title: peca.rotulo || peca.nome,
    subtitle: ROTULO_STATUS_PECA[peca.status] || "",
    groupLabel: peca.ambienteRotulo || "Sem ambiente",
    tone: peca.atrasada ? "danger" : TOM_STATUS_PECA[peca.status] || "neutral",
    previsao: base === "prevista",
    payload: { tipo: "peca", peca },
  };
}

/**
 * {eventos, semData, vocab} para o components/Calendar. `semData` diz
 * quantos itens ficaram de fora por não ter data na base escolhida.
 */
export function calendarioDoSetor(entidade, { demandas = [], tarefas = [], pecas = [], base = "limite", hoje = new Date() }) {
  if (entidade === "lote") {
    const lotes = agruparPorLote(demandas);
    const eventos = lotes.map((l) => eventoDoLote(l, base));
    return {
      eventos: eventos.filter(Boolean),
      semData: eventos.filter((e) => !e).length,
      vocab: VOCAB_LOTE,
    };
  }
  if (entidade === "tarefa") {
    const { eventos, semData } = eventosDoCalendario([], tarefas, { base, hoje });
    return { eventos, semData: semData.tarefas, vocab: VOCAB_TAREFA };
  }
  if (entidade === "peca") {
    const eventos = pecas.map((p) => eventoDaPeca(p, base));
    return { eventos: eventos.filter(Boolean), semData: eventos.filter((e) => !e).length, vocab: VOCAB_PECA };
  }
  const { eventos, semData } = eventosDoCalendario(demandas, [], { base, hoje });
  return { eventos, semData: semData.ambientes, vocab: VOCAB_AMBIENTE };
}

// --------------------------------------------------------------- timeline

// Chips de tipo da linha do tempo (categoria do item -> rótulo e tom).
export const CATEGORIAS_TIMELINE = Object.freeze([
  { valor: "planilha", rotulo: "Planilha", tom: "neutral" },
  { valor: "alocacao", rotulo: "Distribuição", tom: "neutral" },
  { valor: "status", rotulo: "Status", tom: "info" },
  { valor: "prazo", rotulo: "Prazos", tom: "warning" },
  { valor: "tarefa", rotulo: "Tarefas", tom: "neutral" },
  { valor: "peca", rotulo: "Peças", tom: "neutral" },
  { valor: "comentario", rotulo: "Comentários", tom: "neutral" },
  { valor: "cadastro", rotulo: "Cadastros", tom: "neutral" },
]);

// Qual `alvo` de evento pertence a cada entidade da tela. O ambiente (e o
// lote, que é um agrupamento dele) fica com tudo que não é de tarefa nem de
// peça — inclusive os cadastros do setor, que contam a história da sala.
function eventoDaEntidade(evento, entidade) {
  if (entidade === "tarefa") return evento.alvo === "tarefa";
  if (entidade === "peca") return evento.alvo === "peca";
  return evento.alvo !== "tarefa" && evento.alvo !== "peca";
}

/**
 * Itens da linha do tempo do setor para a entidade:
 * - ambientes/lotes: o que aconteceu com os ambientes (e a chegada deles
 *   ao setor pela planilha) + cadastros do setor;
 * - tarefas: só o que aconteceu com as tarefas;
 * - peças: só o que aconteceu com as peças desmembradas.
 * Com filtro ativo (`filtrando`), só entra o que é dos ambientes/tarefas/peças
 * que passaram no filtro — a linha do tempo conversa com o resto da tela.
 */
export function timelineDoSetor({
  eventos = [],
  demandas = [],
  tarefas = [],
  pecas = [],
  entidade = "ambiente",
  filtrando = false,
}) {
  const doApp = eventos.filter((e) => eventoDaEntidade(e, entidade));
  const daPlanilha = entidade === "ambiente" || entidade === "lote" ? demandas : [];
  const montados = montarTimelineSetor(doApp, daPlanilha);
  // Ver por LOTE: só o que é de ambiente com lote, com o lote no título — antes
  // era a mesma lista dos ambientes, e o seletor não mudava nada.
  const itens = entidade === "lote" ? doLote(montados, demandas) : montados;
  if (!filtrando) return itens;
  const chaves = new Set(demandas.map((d) => d.chave).filter(Boolean));
  const idsTarefas = new Set(tarefas.map((t) => t.id));
  const idsPecas = new Set(pecas.map((p) => p.id));
  return itens.filter((item) => {
    if (item.pecaId) return idsPecas.has(item.pecaId);
    if (item.tarefaId) return idsTarefas.has(item.tarefaId);
    if (item.chave) return chaves.has(item.chave);
    return false;
  });
}

function doLote(itens, demandas) {
  const lotePorChave = new Map(demandas.filter((d) => d.chave && d.lote).map((d) => [d.chave, d.lote]));
  return itens
    .filter((item) => item.chave && lotePorChave.has(item.chave))
    .map((item) => ({ ...item, titulo: `Lote ${lotePorChave.get(item.chave)} · ${item.titulo}` }));
}

/** Contagem por categoria, para os chips. */
export function contarPorCategoria(itens) {
  const contagem = {};
  itens.forEach((item) => {
    contagem[item.categoria] = (contagem[item.categoria] || 0) + 1;
  });
  return contagem;
}
